/**
 * @file Text2SpeechProcessor.ts
 *
 * Concrete TTS processor used by `SessionContext` for non-hybrid (stable-only or
 * `onlyStableModel`) sessions.
 *
 * Responsibilities:
 * - Accepts plain text strings via `enqueueText(text)` and pushes them onto the inherited
 *   `pendingText2SpeechQueue`.
 * - `start()` launches a 100 ms polling interval. Each tick is a no-op if the
 *   `AudioPlayer` is currently playing; otherwise it dequeues the next item and calls the
 *   private `speak()` method, which builds SSML via `buildSsml()` and forwards it to the
 *   Azure `SpeechSynthesizer`. Audio returned by the synthesizer is handed directly to
 *   `AudioPlayer.play()`.
 * - In `textOnly` mode, `start()` returns immediately and `speak()` drops synthesizer
 *   output before it reaches the device.
 *
 * Deduplication — `processAlphabeticLanguage()`:
 *   Used for all languages except Urdu and `onlyStableModel` mode. Applies a
 *   Levenshtein-based overlap check to prevent re-speaking the leading words of a sentence
 *   that have already been uttered:
 *     1. Split the incoming recognized text into sentence tokens on punctuation boundaries.
 *     2. Compare the first N words of the latest sentence against the last entry in
 *        `currentRecognizedTokens` (where N = word-count of the last spoken fragment).
 *     3. If the Levenshtein distance is < 3 (i.e. the leading words are essentially the
 *        same), speak only the suffix that follows those N words.
 *     4. If the distance is >= 3, treat the sentence as entirely new and speak it in full.
 *
 * Partial recognition — `processPartialLegacy()`:
 *   Index-based token tracking. Splits partial text on punctuation; for each token at
 *   position `i`, speaks the token only if position `i` has not been recorded yet AND the
 *   token ends with a punctuation character, preventing mid-sentence partial reads.
 *
 * Pipeline position:
 *   SessionContext (recognized / translated text)
 *     → Text2SpeechProcessor.enqueueText()
 *       → 100 ms polling → speak() → Azure SpeechSynthesizer → AudioPlayer
 */
import {Output} from '../contexts/Configuration';
import {levenshtein} from '../contexts/Levenshtein';
import {BaseSpeechProcessor} from "./SpeechProcessor";
import BenchmarkCollector from "../benchmark/BenchmarkCollector";

export class Text2SpeechProcessor extends BaseSpeechProcessor {

    /**
     * Add text to the processor queue.
     * A poller eventually will take this text and perform the following tasks:
     * - Add ssml to the text
     * - Send it to the Text2Speech and get a Speech audio back
     * - Enqueue in the Audio Output the audio
     * @param textToSpeak
     */
    public enqueueText = (textToSpeak: string): void => {
        console.log('[Text2SpeechProcessor] Queueing T2S services...', textToSpeak)
        BenchmarkCollector.instance().captureTtsQueueEntry(this.outputConfig.language.translationKey, textToSpeak);
        this.pendingText2SpeechQueue.enqueue(textToSpeak);
    }

    /**
     *
     */
    public start(): void {
        if (this.outputConfig.textOnly)
            return;

        if (this.pollingInterval) {
            throw new Error('[Text2SpeechProcessor] Cant start Text2Speech processor. There\'s already a polling interval running.');
        }

        this.pollingInterval = setInterval(() => {
            if (this.audioPlayer?.playing())
                return;


            let textToSpeak = this.pendingText2SpeechQueue.dequeue();
            if (!!textToSpeak)
                this.speak(textToSpeak);

        }, 100)

        this.audioPlayer?.start();
    }



    public getOutputConfig(): Output {
        return this.outputConfig;
    }

    private speak(textToSpeak: string) {

        const sendToDevice = (data: ArrayBuffer) => {
            if (this.outputConfig.textOnly)
                return;

            this.audioPlayer?.play(data)
        }

        const ssml = this.buildSsml(textToSpeak, this.outputConfig.language.speakKey, this.outputConfig.language.voices[this.outputConfig.voiceIndex].name, this.outputConfig.pace);

        this.speechSynthesizer.speakSsmlAsync(ssml, (speechResult => {
            console.log('[Text2SpeechProcessor] Speech Ready', speechResult, speechResult.audioData);
            sendToDevice(speechResult.audioData);
        }), err => {
            console.log('[Text2SpeechProcessor] Speech error', err);
        });
    }


    public processRecognition(modelType: string, partiallyRecognizedText: string, onRecognitionReady: (recognizedText: string) => void) {

        console.log('[Text2SpeechProcessor] Processing recognition... ModelType:', modelType)
        if ('onlyStableModel' !== modelType && 'ur' !== this.outputConfig.language.translationKey) {
            this.processAlphabeticLanguage(true, partiallyRecognizedText, onRecognitionReady);
            this.currentRecognizedTokens = [];
        } else {
            this.processLanguageWithoutPunctuation(true, partiallyRecognizedText, onRecognitionReady);
        }
    }

    public processLanguageWithoutPunctuation(isStable: boolean, recognizedText: string, onRecognitionReady: (recognizedText: string) => void) {
        //console.log('About to say: [', recognizedText)
        onRecognitionReady(recognizedText);
    }

    public processAlphabeticLanguage(isStable: boolean, partiallyRecognizedText: string, onRecognitionReady: (recognizedText: string) => void) {

        if (!this.currentRecognizedTokens)
            this.currentRecognizedTokens = [];
        //console.log('processAlphabeticLanguage 1', partiallyRecognizedText);
        const tokens = partiallyRecognizedText.match(/[^.;?!]+[.;?!]?/g);
        if (!tokens) return;
        //console.log('processAlphabeticLanguage 1.b', tokens);
        let lastSentenceOfTheRecognizing = tokens[tokens.length - (isStable ? 1 : 2)];
        //console.log('processAlphabeticLanguage 2', lastSentenceOfTheRecognizing);
        if (!lastSentenceOfTheRecognizing || (!isStable && !['.', ';', '?', '!', '、', '。', '।'].includes(lastSentenceOfTheRecognizing.charAt(lastSentenceOfTheRecognizing.length - 1))))
            return;

        let wordsToSay = '';

        //console.log('processAlphabeticLanguage 3');

        //1. tokenizo por espacio, cuantas palabras en lastSaid? -> N
        //2. dame las N primeras palabras de lastSentences
        //3. aplico leihman entre los dos conjuntos
        //4. si paso el score de igualdad -> descarto N de lastSentenceOfTheRecognizing, y digo lo que queda y sumo eso al array de dichos
        //lo digo en su totalidad, lo sumo al array de dichos, sigo con mi vida
        if (this.currentRecognizedTokens.length === 0) {
            //console.log('processAlphabeticLanguage 3.b first words ', lastSentenceOfTheRecognizing);
            wordsToSay = lastSentenceOfTheRecognizing;
        } else {

            let lastSaid = this.currentRecognizedTokens[this.currentRecognizedTokens.length - 1]; //1.
            let lastSaidWordCount = lastSaid.split(' ').length; //1.
            let firstWordsOfRecognizing = ''; //2.
            //console.log('processAlphabeticLanguage 4');
            let lastSentenceOfTheRecognizingSplitted = lastSentenceOfTheRecognizing.split(' ');
            let limit = lastSaidWordCount <= lastSentenceOfTheRecognizingSplitted.length ? lastSaidWordCount : lastSentenceOfTheRecognizingSplitted.length
            for (let i = 0; i < (limit); i++) {
                firstWordsOfRecognizing = firstWordsOfRecognizing + lastSentenceOfTheRecognizingSplitted[i] + ' ';
            }
            firstWordsOfRecognizing = firstWordsOfRecognizing.trimEnd()
            //console.log('processAlphabeticLanguage 5');

            //console.log('Comparing: [', firstWordsOfRecognizing, '] vs [', lastSaid, ']')

            let similarityScore = levenshtein(firstWordsOfRecognizing, lastSaid); //3.
            //console.log('Levenshtein score: ', similarityScore)


            if (similarityScore < 3) {//4.
                //similar phrases, avoid repeating first words
                if (lastSentenceOfTheRecognizingSplitted.length === lastSaidWordCount) {
                    //nothing to say? shouldn't happen
                    return;
                }

                for (let i = lastSaidWordCount; i < lastSentenceOfTheRecognizingSplitted.length; i++) {
                    wordsToSay = wordsToSay + lastSentenceOfTheRecognizingSplitted[i] + ' ';
                }
                wordsToSay = wordsToSay.trimEnd()
            } else {
                wordsToSay = lastSentenceOfTheRecognizing;
            }
        }

        //console.log('About to say: [', wordsToSay)

        this.currentRecognizedTokens.push(wordsToSay);
        //console.log('Pushing token: ', wordsToSay)
        onRecognitionReady(wordsToSay);
    }

    public processPartialRecognition(partiallyRecognizedText: string, onRecognitionReady: (recognizedText: string) => void) {
        this.processPartialLegacy(partiallyRecognizedText, onRecognitionReady);
    }

    public processPartialLegacy(partiallyRecognizedText: string, onRecognitionReady: (recognizedText: string) => void) {
        if (!this.currentRecognizedTokens)
            this.currentRecognizedTokens = [];

        let length = this.currentRecognizedTokens.length;

        //hola como estas? bien bien. soy pepe. y vos?
        //hola
        //hola como estas
        //hola como estas? bien
        //Hola como estás? bien bien
        //Hola como estás? bien bien. soy pepe
        //Hola como estás? bien bien. soy pepe. y

        //recognized
        //Hola como estás? Bien bien soy Pepe. y vos?

        //Leihman

        const tokens = partiallyRecognizedText.match(/[^.;?!]+[.;?!]?/g);

        if (!tokens) return;
        //hola. i 0 l 1
        //hola. dada i 0 i 1 l 2
        for (let i = 0; i < (tokens?.length || 0); i++) {
            const token = tokens[i];

            // Check if the token at this index has been processed before
            if (!this.currentRecognizedTokens[i]) {

                if (!['.', ';', '?', '!'].includes(token.charAt(token.length - 1))) return;
                // Process the token using translationProcessor
                //this.translationProcessor.enqueueText(token);
                // Add the token's index to the map
                this.currentRecognizedTokens.push(token);
                console.log('Pushing token: ', token)
                onRecognitionReady(token);
            }
        }
    }
}
